import { Observable, Observer } from 'rxjs';
import { inject, Injectable, NgZone } from '@angular/core';
import { SseFetchInterceptorService } from '../../core/interceptors/sse.fetch.interceptor';

@Injectable({ providedIn: 'root' })
export class SseClientService {
  private readonly zone = inject(NgZone);
  private readonly sseFetch = inject(SseFetchInterceptorService);

  private readonly RETRY_DELAY_MS = 30000; // 30 s
  private readonly MAX_RETRY_TIME_MS = 300000; // 5 min

  public stream<T>(url: string): Observable<T> {
    return new Observable<T>((observer: Observer<T>) => {
      const controller = new AbortController();
      let active = true;
      let retryWindowStartedAt: number | null = null;
      let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

      const clearReconnectTimeout = () => {
        if (reconnectTimeout !== null) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
      };

      const connect = () => {
        if (!active || controller.signal.aborted) {
          return;
        }

        this.startStream(url, observer, controller, () => {
          retryWindowStartedAt = null;
        }).catch((err) => {
          if (!active || controller.signal.aborted) {
            return;
          }

          console.error('SSE stream error:', err);

          retryWindowStartedAt ??= Date.now();
          const elapsed = Date.now() - retryWindowStartedAt;

          if (err?.message === 'Unauthorized' || elapsed >= this.MAX_RETRY_TIME_MS) {
            observer.error(err); // Close stream
            return;
          }

          clearReconnectTimeout();
          reconnectTimeout = setTimeout(connect, this.RETRY_DELAY_MS);
        });
      };

      connect();

      return () => {
        active = false;
        controller.abort();
        clearReconnectTimeout();
      };
    });
  }

  private async startStream<T>(
    url: string,
    observer: Observer<T>,
    controller: AbortController,
    onConnected: () => void,
  ) {
    const response = await this.sseFetch.fetch(url, controller);

    if (!response.body) {
      throw new Error('SSE stream not available.'); // Retry
    }

    onConnected();

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (!controller.signal.aborted) {
      const { done, value } = await reader.read();

      if (done) {
        observer.complete();
        return;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = this.processChunks(buffer, observer);
    }
  }

  private processChunks<T>(buffer: string, observer: Observer<T>): string {
    const chunks = buffer.split('\n\n');

    for (let i = 0; i < chunks.length - 1; i++) {
      const chunk = chunks[i].trim();
      if (!chunk.startsWith('data:')) {
        continue;
      }

      const jsonText = chunk.replace('data:', '').trim();

      try {
        const parsed: T = JSON.parse(jsonText);
        this.zone.run(() => observer.next(parsed));
      } catch (err) {
        console.error('SSE JSON parse error:', err, jsonText);
      }
    }

    return chunks[chunks.length - 1];
  }
}
